import { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import { formatPrice, imageFallback, itemKey } from "../utils/store";
import "../css/header.scss";

export function Icon({ name, ...props }) {
  const paths = {
    search: (
      <>
        <circle cx="10.8" cy="10.8" r="7.8" />
        <path d="m16.5 16.5 5 5" />
      </>
    ),
    bag: (
      <>
        <path d="M4.5 7.5h15l1 14h-17l1-14Z" />
        <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      </>
    ),
    heart: (
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    ),
    close: <path d="m5 5 14 14M19 5 5 19" />,
    menu: <path d="M2 5h20M2 12h20M2 19h20" />,
    arrow: <path d="M3 12h18m-7-7 7 7-7 7" />,
    chevron: <path d="m7 9 5 5 5-5" />,
    check: <path d="m4 12 5 5L20 6" />,
    filter: (
      <>
        <path d="M3 6h18M3 18h18" />
        <path d="M8 3v6m8 6v6" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="1" />
        <path d="M8 10V6a4 4 0 0 1 8 0v4m-4 5v2" />
      </>
    ),
  };
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      {...props}
    >
      {paths[name] || paths.arrow}
    </svg>
  );
}

export default function Header() {
  const {
    cartCount,
    cartOpen,
    setCartOpen,
    lines,
    subtotal,
    removeFromCart,
    updateQuantity,
    wishlist,
  } = useContext(CartContext);
  const [panel, setPanel] = useState(null);
  const [dropdown, setDropdown] = useState(null);
  const [query, setQuery] = useState("");
  const dialog = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const activePanel = cartOpen ? "cart" : panel;
  const results = query.trim()
    ? productData
        .filter((p) =>
          `${p.name} ${p.category} ${p.tags.join(" ")}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
        )
        .slice(0, 5)
    : [];
  useEffect(() => {
    setPanel(null);
    setDropdown(null);
    setCartOpen(false);
  }, [location.pathname, location.search, location.hash, setCartOpen]);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (activePanel && !element.open) element.showModal();
    if (!activePanel && element.open) element.close();
  }, [activePanel]);
  useEffect(() => {
    const closeMenu = (event) => {
      if (event.key === "Escape") setDropdown(null);
    };
    document.addEventListener("keydown", closeMenu);
    return () => document.removeEventListener("keydown", closeMenu);
  }, []);
  const close = () => {
    setPanel(null);
    setCartOpen(false);
  };
  const openPanel = (value) => {
    setDropdown(null);
    setCartOpen(false);
    setPanel(value);
  };
  const search = (event) => {
    event.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
    close();
  };

  if (location.pathname === "/checkout") return null;
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="header-inner">
          <button
            className="icon-button mobile-menu-toggle"
            aria-label="Open navigation menu"
            onClick={() => openPanel("menu")}
          >
            <Icon name="menu" />
          </button>
          <Link
            className="brand-wordmark"
            to="/"
            aria-label={`${siteConfig.brandName} home`}
          >
            <img src={siteConfig.logo.image} alt="" />
          </Link>
          <nav className="desktop-nav" aria-label="Main navigation">
            <button
              className={
                dropdown === "shop" ? "nav-button active" : "nav-button"
              }
              aria-expanded={dropdown === "shop"}
              onClick={() => setDropdown(dropdown === "shop" ? null : "shop")}
            >
              Shop
            </button>
            <button
              className={
                dropdown === "studio" ? "nav-button active" : "nav-button"
              }
              aria-expanded={dropdown === "studio"}
              onClick={() =>
                setDropdown(dropdown === "studio" ? null : "studio")
              }
            >
              Studio
            </button>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/contact">Contact</NavLink>
            <button
              className={
                dropdown === "more" ? "nav-button active" : "nav-button"
              }
              aria-expanded={dropdown === "more"}
              onClick={() => setDropdown(dropdown === "more" ? null : "more")}
            >
              More
            </button>
          </nav>
          <div className="header-actions">
            <span className="header-language">English</span>
            <button
              className="icon-button"
              aria-label="Search"
              onClick={() => openPanel("search")}
            >
              <Icon name="search" />
            </button>
            <Link
              className="icon-button wishlist-link"
              to="/shop?saved=true"
              aria-label={`Wishlist, ${wishlist.length} saved items`}
            >
              <Icon name="heart" />
              {wishlist.length > 0 && (
                <span className="count-dot">{wishlist.length}</span>
              )}
            </Link>
            <button
              className="icon-button cart-toggle"
              aria-label={`Open cart, ${cartCount} items`}
              onClick={() => {
                setPanel(null);
                setCartOpen(true);
                setDropdown(null);
              }}
            >
              <Icon name="bag" />
              {cartCount > 0 && <span className="count-dot">{cartCount}</span>}
            </button>
          </div>
        </div>
        {dropdown && (
          <>
            <button
              className="mega-backdrop"
              aria-label="Close navigation"
              onClick={() => setDropdown(null)}
            />
            <div className="mega-menu">
              {dropdown === "shop" ? (
                <>
                  <div className="mega-links">
                    <h3>Shop by style</h3>
                    {siteConfig.categories.map((category) => (
                      <Link
                        key={category}
                        to={`/shop?category=${encodeURIComponent(category)}`}
                      >
                        {category}
                      </Link>
                    ))}
                    <Link to="/shop">All shoes</Link>
                  </div>
                  <Link
                    className="mega-feature"
                    to="/shop?collection=arenviko%20Core%20Collection"
                  >
                    <img
                      src="/img/editorial/header-core.jpg"
                      alt="arenviko everyday loafers"
                    />
                    <h3>arenviko Core Collection</h3>
                    <p>Versatile shapes for everyday movement</p>
                  </Link>
                </>
              ) : dropdown === "studio" ? (
                <>
                  <div className="mega-links">
                    <h3>From arenviko Studio</h3>
                    <Link to="/shop?collection=arenviko%20Studio%20Collection">
                      arenviko Studio Collection
                    </Link>
                    <Link to="/about#craft">Our design approach</Link>
                    <Link to="/contact">Ask for style guidance</Link>
                  </div>
                  <Link
                    className="mega-feature"
                    to="/shop?collection=arenviko%20Studio%20Collection"
                  >
                    <img
                      src="/img/editorial/header-studio.jpg"
                      alt="arenviko Studio Oxford shoes"
                    />
                    <h3>arenviko Studio Collection</h3>
                    <p>Refined details and confident colour</p>
                  </Link>
                </>
              ) : (
                <>
                  <div className="mega-links">
                    <h3>Help &amp; policies</h3>
                    <Link to="/faqs">FAQs</Link>
                    <Link to="/shipping">Shipping &amp; Handling</Link>
                    <Link to="/returns">Return Policy</Link>
                    <Link to="/privacy">Privacy Policy</Link>
                    <Link to="/cookies">Cookie Policy</Link>
                    <Link to="/terms">Terms of Service</Link>
                  </div>
                  <div className="mega-feature mega-feature-copy">
                    <p className="eyebrow">Need a hand?</p>
                    <h3>Questions before you order</h3>
                    <p>Read common answers or contact arenviko for personal guidance.</p>
                    <Link className="text-link" to="/contact">Contact us</Link>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </header>
      <dialog
        ref={dialog}
        className={`header-dialog ${activePanel === "search" ? "search-dialog" : "side-drawer"}`}
        onCancel={close}
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
        aria-labelledby="panel-title"
      >
        <div className="drawer-shell">
          <div className="drawer-heading">
            <h2 id="panel-title">
              {activePanel === "cart"
                ? `Cart (${cartCount})`
                : activePanel === "search"
                  ? "Search"
                  : "Menu"}
            </h2>
            <button
              className="icon-button"
              onClick={close}
              aria-label="Close panel"
            >
              <Icon name="close" />
            </button>
          </div>
          {activePanel === "menu" && (
            <nav className="mobile-nav" aria-label="Mobile navigation">
              <Link to="/shop">Shop all shoes</Link>
              <details>
                <summary>Readymade shoes</summary>
                {siteConfig.categories.map((category) => (
                  <Link
                    key={category}
                    to={`/shop?category=${encodeURIComponent(category)}`}
                  >
                    {category}
                  </Link>
                ))}
              </details>
              <Link to="/shop?collection=arenviko%20Studio%20Collection">
                Studio Collection
              </Link>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
              <details>
                <summary>More</summary>
                <Link to="/faqs">FAQs</Link>
                <Link to="/shipping">Shipping</Link>
                <Link to="/returns">Returns</Link>
                <Link to="/privacy">Privacy</Link>
                <Link to="/cookies">Cookies</Link>
                <Link to="/terms">Terms</Link>
              </details>
              <Link to="/shop?saved=true">Wishlist ({wishlist.length})</Link>
              <a
                className="mobile-email"
                href={`mailto:${siteConfig.contact.email}`}
              >
                {siteConfig.contact.email}
              </a>
            </nav>
          )}
          {activePanel === "search" && (
            <div className="search-content">
              <form onSubmit={search} className="search-form">
                <Icon name="search" />
                <input
                  aria-label="Search products"
                  placeholder="Search for…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                <button className="icon-button" aria-label="Submit search">
                  <Icon name="arrow" />
                </button>
              </form>
              {!query.trim() ? (
                <div className="search-suggestions">
                  <p className="eyebrow">Explore the collections</p>
                  {siteConfig.categories.map((category) => (
                    <Link
                      key={category}
                      to={`/shop?category=${encodeURIComponent(category)}`}
                    >
                      {category}
                    </Link>
                  ))}
                </div>
              ) : (
                <>
                  <p className="small muted search-caption">
                    {results.length
                      ? "Products"
                      : `No results for “${query}”. Try a style or colour.`}
                  </p>
                  <div className="search-results">
                    {results.map((product) => (
                      <Link to={`/shopDetails/${product.id}`} key={product.id}>
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          onError={imageFallback}
                        />
                        <span>
                          {product.name}
                          <small>{formatPrice(product.price)}</small>
                        </span>
                        <Icon name="arrow" />
                      </Link>
                    ))}
                  </div>
                  {results.length > 0 && (
                    <Link
                      className="text-link"
                      to={`/shop?q=${encodeURIComponent(query.trim())}`}
                    >
                      View all results
                    </Link>
                  )}
                </>
              )}
            </div>
          )}
          {activePanel === "cart" &&
            (lines.length ? (
              <>
                <div className="drawer-items">
                  {lines.map((item) => (
                    <article className="drawer-item" key={itemKey(item)}>
                      <Link to={`/shopDetails/${item.id}`}>
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          onError={imageFallback}
                        />
                      </Link>
                      <div>
                        <Link
                          className="drawer-product-title"
                          to={`/shopDetails/${item.id}`}
                        >
                          {item.product.name}
                        </Link>
                        <p className="small muted">
                          {item.size} UK / {item.color}
                        </p>
                        <p className="small">
                          {formatPrice(item.product.price)}
                        </p>
                        <div className="drawer-item-actions">
                          <div className="quantity-control">
                            <button
                              aria-label={`Decrease ${item.product.name} quantity`}
                              disabled={item.quantity <= 1}
                              onClick={() =>
                                updateQuantity(
                                  item.id,
                                  item.size,
                                  item.color,
                                  item.quantity - 1,
                                )
                              }
                            >
                              −
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              aria-label={`Increase ${item.product.name} quantity`}
                              disabled={item.quantity >= 10}
                              onClick={() =>
                                updateQuantity(
                                  item.id,
                                  item.size,
                                  item.color,
                                  item.quantity + 1,
                                )
                              }
                            >
                              +
                            </button>
                          </div>
                          <button
                            className="text-link small"
                            onClick={() =>
                              removeFromCart(item.id, item.size, item.color)
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="drawer-bottom">
                  <div className="drawer-subtotal">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <p className="small muted">
                    Shipping calculated at checkout.
                  </p>
                  <Link className="button button--full" to="/checkout">
                    Checkout <span>·</span> {formatPrice(subtotal)}
                  </Link>
                  <Link className="text-link small" to="/cartpage">
                    View cart
                  </Link>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <Icon name="bag" />
                <h3>Your cart is empty</h3>
                <Link className="button" to="/shop">
                  Explore our shoes
                </Link>
              </div>
            ))}
        </div>
      </dialog>
    </>
  );
}
